/**
 * BTU Calculation Logic for HVAC Sizing
 * Based on industry-standard formulas and multipliers
 */

export const calculateBTU = (formData) => {
  // Extract form data
  const {
    roomLength,
    roomWidth,
    roomHeight,
    insulationQuality,
    buildingAge,
    sunExposure,
    numberOfOccupants,
    heatAppliances,
    activityLevel,
    hasKitchen,
    outsideTemp,
    insideTemp,
    humidityLevel,
    floorLocation
  } = formData;
  
  // Calculate room dimensions
  const length = parseFloat(roomLength) || 0;
  const width = parseFloat(roomWidth) || 0;
  const height = parseFloat(roomHeight) || 2.7;
  
  const roomArea = length * width;
  const roomVolume = roomArea * height;
  
  // Base BTU calculation: Room Volume × 200 BTU per cubic meter
  const baseBTU = roomVolume * 200;
  
  // Insulation multipliers
  const insulationMultipliers = {
    poor: 1.5,
    average: 1.25,
    good: 1.1,
    excellent: 1.0
  };
  const insulationFactor = insulationMultipliers[insulationQuality] || 1.25;
  
  // Sun exposure multipliers
  const sunMultipliers = {
    none: 1.0,
    partial: 1.1,
    full: 1.2
  };
  const sunFactor = sunMultipliers[sunExposure] || 1.1;
  
  // Building age multipliers
  const ageMultipliers = {
    'new': 1.0,
    '5-15': 1.1,
    '15-30': 1.2,
    '30+': 1.3
  };
  const ageFactor = ageMultipliers[buildingAge] || 1.1;
  
  // Floor location adjustment
  const floorMultipliers = {
    ground: 1.05,
    upper: 1.0,
    basement: 0.95,
    top: 1.15  // Top floor gets more heat from roof
  };
  const floorFactor = floorMultipliers[floorLocation] || 1.0;
  
  // Apply multipliers to base calculation
  let adjustedBTU = baseBTU * insulationFactor * sunFactor * ageFactor * floorFactor;
  
  // Fixed additions
  const occupantsBTU = numberOfOccupants * 600;
  const appliancesBTU = heatAppliances * 500;
  const kitchenBTU = hasKitchen ? 1000 : 0;
  
  // Activity level adjustment
  const activityMultipliers = {
    low: 1.0,
    moderate: 1.0,
    high: 1.2
  };
  const activityFactor = activityMultipliers[activityLevel] || 1.0;
  const activityAdjustment = activityFactor > 1.0 ? adjustedBTU * 0.2 : 0;
  
  // Humidity adjustment
  const humidityBTU = humidityLevel === 'high' ? adjustedBTU * 0.1 : 0;
  
  // Temperature difference factor
  const tempDiff = outsideTemp - insideTemp;
  const tempFactor = tempDiff > 15 ? 1.05 : 1.0;
  
  // Calculate total
  const totalBTU = Math.round(
    (adjustedBTU * tempFactor) + 
    occupantsBTU + 
    appliancesBTU + 
    kitchenBTU + 
    activityAdjustment + 
    humidityBTU
  );
  
  // Convert to kW (1 kW = 3412 BTU)
  const totalKW = totalBTU / 3412;
  
  // Breakdown for display
  const breakdown = {
    base: Math.round(baseBTU),
    insulation: Math.round(baseBTU * (insulationFactor - 1)),
    sun: Math.round(baseBTU * (sunFactor - 1)),
    occupants: occupantsBTU,
    appliances: appliancesBTU,
    other: Math.round(kitchenBTU + activityAdjustment + humidityBTU)
  };
  
  // Calculate confidence level based on completeness of data
  let confidence = 85; // Base confidence
  if (insulationQuality !== 'average') confidence += 5;
  if (numberOfOccupants > 0) confidence += 5;
  if (heatAppliances > 0) confidence += 5;
  confidence = Math.min(confidence, 95); // Cap at 95%
  
  return {
    totalBTU,
    totalKW,
    roomArea,
    roomVolume,
    breakdown,
    confidence
  };
};